import { z } from 'zod';
import { memberQuitDateLocalDaySchema, memberQuitDateSourceSchema } from './quit-dates';
import { quitDateDayOffset } from './quit-date-state';

// ─── member.quit_date.changed ───────────────────────────────────────────────
//
// The payload of the event a quit date write publishes, once per appended
// `member_quit_dates` row (ADR-052, ADR-128). Delivery is at-least-once and
// unordered, so a consumer that needs the member's CURRENT date re-reads it
// through the quit date resolver and is idempotent on the envelope id; the
// dates here describe the change, for audit and analytics.
//
// Only closed dimensions are forwarded to behavioural analytics: the source,
// whether this is the first date, and how far from the member's day the new
// date lies, as a bucket. The dates themselves and the episode id stay on the
// event, never a lifted property; the writer's identity stays on the row.

/**
 * How far the new date lies from the member's `today`, as a bucket. Every
 * bucket but `today` spans several days, so with the event time it narrows the
 * date to a window rather than naming it; `today` does name it, as the event's
 * own day, which analytics already holds as the event time. The clinical
 * guidance is a quit day 7 to 14 days out, so that window has its own bucket.
 */
export const QUIT_DATE_OFFSET_BUCKETS = [
  'past_over_30',
  'past_1_30',
  'today',
  '1_7',
  '8_14',
  '15_30',
  '31_90',
  'over_90',
] as const;
export const quitDateOffsetBucketSchema = z.enum(QUIT_DATE_OFFSET_BUCKETS);
export type QuitDateOffsetBucket = z.infer<typeof quitDateOffsetBucketSchema>;

/**
 * The bucket for a quit date seen from the member's `today`. Both must be
 * parsed `YYYY-MM-DD` days; anything else throws, as for the state derivation.
 */
export function quitDateOffsetBucket(quitDate: string, today: string): QuitDateOffsetBucket {
  const days = quitDateDayOffset(quitDate, today);
  if (days < -30) return 'past_over_30';
  if (days < 0) return 'past_1_30';
  if (days === 0) return 'today';
  if (days <= 7) return '1_7';
  if (days <= 14) return '8_14';
  if (days <= 30) return '15_30';
  if (days <= 90) return '31_90';
  return 'over_90';
}

/**
 * Strict on purpose, like the care-pathway payload: this is what the producer
 * validates before it enqueues, so a writer field leaking in (`recordedBy`, a
 * staff id) fails the parse instead of reaching the bus.
 *
 * `quitDate` is null only for a clear, which no surface can make today; it is
 * nullable now so consumers handle it from their first release. The bucket is
 * null exactly when the date is.
 *
 * The row's `source` travels as `quitDateSource`: the analytics forwarder
 * bounds a lifted value by its data key across every event, so a bare `source`
 * would share one bound with every other event's source vocabulary.
 */
export const memberQuitDateChangedPayloadSchema = z
  .object({
    memberProgramId: z.string().uuid(),
    previousQuitDate: memberQuitDateLocalDaySchema.nullable(),
    quitDate: memberQuitDateLocalDaySchema.nullable(),
    quitDateSource: memberQuitDateSourceSchema,
    isFirstDate: z.boolean(),
    quitDateOffsetBucket: quitDateOffsetBucketSchema.nullable(),
    // Injected by buildAnnotatedDomainEvent from the analytics catalogue —
    // closed to that exact value, never free text.
    status: z.literal('updated').optional(),
  })
  .strict()
  .refine((data) => (data.quitDate === null) === (data.quitDateOffsetBucket === null), {
    message: 'quitDateOffsetBucket is null exactly when quitDate is null',
    path: ['quitDateOffsetBucket'],
  });
export type MemberQuitDateChangedPayload = z.infer<typeof memberQuitDateChangedPayloadSchema>;
